import {
  Box,
  Divider,
  Drawer,
  Fab,
  List,
  ListItemButton,
  Stack,
  Typography,
} from '@mui/material'
import { FC, useCallback, useMemo, useState } from 'react'
import { Icons } from 'utoniq-ui'

import {
  Artist,
  Maybe,
  MembershipSubscriptionProduct,
  PostBoxFragment,
  PostType,
} from '@/clients/utoniq-core/schema'
import {
  ArtistHeaderItemsLeft,
  ArtistHeaderItemsLeftProps,
  ArtistHeaderItemsRight,
  Head,
} from '@/components/layouts'
import { ConsoleBaseLayout } from '@/components/layouts/ConsoleBaseLayout'
import { PostBox, PostBoxSkeleton } from '@/components/organisms'
import { ScrollFetcher } from '@/components/organisms/functionals'
import { ArtistThemeProvider, styles } from '@/components/styles'
import { constants } from '@/config'
import { useRouter, useTranslation } from '@/core'
import { Cell, Template } from '@/core/architecture'

import { SearchForm, SearchFormProps } from './SearchForm'

export * from './fragments'

export type State = {
  user: ArtistHeaderItemsLeftProps['currentUser']
  artistId: string
  userId: string
  artist: Maybe<
    Pick<Artist, 'id' | 'paymentType' | 'hasArrangingDeliveryItems'>
  >
  loading: boolean
  hasNextPage: boolean
  loadingDeletePost: boolean
  posts: Maybe<{ edges: { node: PostBoxFragment }[] }>
  membership: Pick<MembershipSubscriptionProduct, 'productId'>[] | undefined
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onFilter: SearchFormProps['onSubmit']
  onFetchMore: () => Promise<void>
  onDeletePost: (id: string) => Promise<void>
}

export const ConsolePostsTemplate: Template<State, Action> = ({
  state,
  onFilter,
  onFetchMore,
  onDeletePost,
}) => {
  const { t } = useTranslation('consolePosts')
  const { routerNames } = useRouter()

  return (
    <ArtistThemeProvider artistId={state.artistId}>
      <Head title={routerNames.ConsolePost} />
      <ConsoleBaseLayout
        header={{
          left: (
            <ArtistHeaderItemsLeft
              currentUser={state.user}
              paymentType={state.artist?.paymentType}
              hasArrangingDeliveryItems={
                state.artist?.hasArrangingDeliveryItems
              }
              membership={state.membership}
            />
          ),
          right: <ArtistHeaderItemsRight artistId={state.artistId} />,
        }}
      >
        <Typography variant="h3" sx={{ py: 3, px: 5 }}>
          {t('postManagement')}
        </Typography>
        <SearchForm
          hiddenFilter={!state.posts?.edges.length}
          onSubmit={onFilter}
        />
        <Stack sx={{ px: 5, pt: 2 }}>
          <ScrollFetcher
            nodes={state.posts?.edges.map(({ node }) => (
              <PostBox
                loadingDeletePost={state.loadingDeletePost}
                onDeletePost={onDeletePost}
                post={node}
                fullWidth
                isConsole
                userId={state.userId}
              />
            ))}
            loading={state.loading}
            hasNextPage={state.hasNextPage}
            onFetchMore={onFetchMore}
            emptyMessage={t('emptyPost')}
            loadingComponent={
              <Stack spacing={3} sx={{ pb: 2 }}>
                {Array.from(Array(3).keys()).map(key => (
                  <PostBoxSkeleton fullWidth key={key} />
                ))}
              </Stack>
            }
          />
        </Stack>
        <BaseCell />
      </ConsoleBaseLayout>
    </ArtistThemeProvider>
  )
}

const BaseCell: Cell = () => {
  const [open, setOpen] = useState<boolean>(false)

  const toggleDrawer = useCallback(
    (open: boolean) => () => {
      setOpen(open)
    },
    []
  )

  return (
    <Stack direction="row-reverse">
      <Stack
        sx={{
          position: 'fixed',
          bottom: 16,
          zIndex: 5,
          maxWidth: 600,
          margin: '0 auto',
          px: 4,
          pb: styles.safeAreaBottom,
        }}
      >
        <Fab size="medium" color="primary" onClick={toggleDrawer(true)}>
          <Icons.ThinPlus fontSize="medium" sx={{ color: 'white' }} />
        </Fab>
      </Stack>
      <CreatePostActionSheet open={open} onClose={toggleDrawer(false)} />
    </Stack>
  )
}

type CreatePostActionSheetProps = {
  open: boolean
  onClose: () => void
  // ドロワーを画面幅いっぱいに広げるかどうか
  fullWidth?: boolean
}
export const CreatePostActionSheet: FC<CreatePostActionSheetProps> = ({
  open,
  onClose,
  fullWidth = false,
}) => {
  const { t } = useTranslation('consolePosts')
  const { router, generatePath, routerPages } = useRouter()
  const items = useMemo(
    () => [
      {
        Icon: Icons.Post,
        text: t('text'),
        href: generatePath(routerPages.ConsolePostCreate, {
          resourceType: PostType.Text,
        }),
      },
      {
        Icon: Icons.Image,
        text: t('image'),
        href: generatePath(routerPages.ConsolePostCreate, {
          resourceType: PostType.Image,
        }),
      },
      {
        Icon: Icons.Audio,
        text: t('sound'),
        href: generatePath(routerPages.ConsolePostCreate, {
          resourceType: PostType.Sound,
        }),
      },
      {
        Icon: Icons.Video,
        text: t('video'),
        href: generatePath(routerPages.ConsolePostCreate, {
          resourceType: PostType.Video,
        }),
      },
    ],
    [generatePath, routerPages.ConsolePostCreate, t]
  )

  return (
    <Drawer
      open={open}
      onClose={onClose}
      sx={{
        // 単に props で指定するだけだと
        // default のスタイルが優先されるため
        // セレクタを用いた CSS 指定を行っている
        '& .MuiBackdrop-root': {
          // 画面幅に依らずバックドロップを画面全体にかける
          maxWidth: '100%',
        },
        '& .MuiPaper-root': {
          maxWidth: fullWidth
            ? // fullWidth 指定がある場合
              // 画面幅に依らずドロワーを画面幅いっぱいに広げる
              '100%'
            : // fullWidth 指定がない場合
              // ドロワーの最大幅を 600 px に制限する
              constants.ui.layout.mainContentMaxWidth,
        },
      }}
    >
      <Box sx={{ p: 5 }}>
        <Typography variant="h6" color="grey.600" align="center">
          {t('selectPostType')}
        </Typography>
      </Box>
      <Divider />
      <List>
        {items.map(({ Icon, text, href }, index) => (
          <ListItemButton
            key={index}
            component={Stack}
            sx={{ height: 52 }}
            spacing={3}
            direction="row"
            onClick={() => router.push(href)}
          >
            <Icon fontSize="small" />
            <Typography variant="h6">{text}</Typography>
          </ListItemButton>
        ))}
        <Divider />
        <ListItemButton
          component={Box}
          sx={{ height: 52 }}
          alignItems="center"
          onClick={onClose}
        >
          <Typography
            variant="h6"
            align="center"
            sx={{ width: '100%', color: 'primary.main' }}
          >
            {t('cancel')}
          </Typography>
        </ListItemButton>
      </List>
    </Drawer>
  )
}
